import { useState, useMemo, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  FormControlLabel,
  Box,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import SelectAllIcon from '@mui/icons-material/SelectAll';
import ClearAllIcon from '@mui/icons-material/ClearAll';
import InfoIcon from '@mui/icons-material/Info';
import { MESSAGES } from '../constants/generateCouponsConstants';
import { CustomButton, CustomCheckbox, CustomTextField, StatusChip } from '../../../common';

const TOTAL_INSTALLMENTS = {
  regular: 428,
  premium: 478,
};

const ConfirmationDialog = ({
  open,
  onClose,
  data,
  onConfirm,
  onNavigate,
  excludedContracts,
  onExcludedContractsChange,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectAll, setSelectAll] = useState(false);
  const [manuallyUnexcluded, setManuallyUnexcluded] = useState([]);

  const filteredContracts = useMemo(() => {
    if (!data?.contracts) return [];

    return data.contracts.filter((contract) => {
      if (!contract.contrato?.contract_number) return true;

      const contractNumber = contract.contrato.contract_number.toString();
      const searchLower = searchTerm.toLowerCase();

      return contractNumber.includes(searchLower);
    });
  }, [data?.contracts, searchTerm]);

  const validContracts = useMemo(() => {
    return filteredContracts
      .filter((contract) => contract.contrato?.contract_number !== undefined)
      .map((contract) => {
        const premiumValue = contract.contrato?.is_premium;
        const isPremium =
          premiumValue === true ||
          premiumValue === 1 ||
          premiumValue === '1' ||
          premiumValue === 'true';
        const totalInstallments = isPremium ? TOTAL_INSTALLMENTS.premium : TOTAL_INSTALLMENTS.regular;
        const issued = Number(contract.cantidad_cupones_emitidos ?? 0);
        const issuedRounded = Math.round(issued * 100) / 100;
        const remainingRaw = totalInstallments - issuedRounded;
        const remainingRounded = Math.round(Math.max(remainingRaw, 0) * 100) / 100;
        const extraRounded = remainingRaw < 0 ? Math.round(Math.abs(remainingRaw) * 100) / 100 : 0;
        const isFinishing = remainingRaw <= 10;

        return {
          ...contract,
          meta: {
            isPremium,
            totalInstallments,
            issuedRounded,
            remainingRounded,
            remainingRaw,
            extraRounded,
            isFinishing,
          },
        };
      });
  }, [filteredContracts]);

  const finishingContractIds = useMemo(() => {
    return validContracts
      .filter((contract) => contract.meta.isFinishing)
      .map((contract) => contract.contrato.uuid);
  }, [validContracts]);

  const sortedContracts = useMemo(() => {
    const sortedValid = [...validContracts].sort((a, b) => {
      if (a.meta.isFinishing && !b.meta.isFinishing) return -1;
      if (!a.meta.isFinishing && b.meta.isFinishing) return 1;
      return 0;
    });

    return [
      ...sortedValid,
      ...filteredContracts.filter((contract) => contract.contrato?.contract_number === undefined),
    ];
  }, [filteredContracts, validContracts]);

  useEffect(() => {
    if (finishingContractIds.length === 0) return;

    const toAdd = finishingContractIds.filter(
      (id) => !excludedContracts.includes(id) && !manuallyUnexcluded.includes(id)
    );
    if (toAdd.length === 0) return;

    onExcludedContractsChange([...excludedContracts, ...toAdd]);
  }, [excludedContracts, finishingContractIds, manuallyUnexcluded, onExcludedContractsChange]);

  const handleCheckboxChange = (contract, isChecked) => {
    const uuid = contract.contrato.uuid;
    const newExcluded = isChecked
      ? [...excludedContracts, uuid]
      : excludedContracts.filter((item) => item !== uuid);

    onExcludedContractsChange(newExcluded);

    if (contract.meta?.isFinishing) {
      if (!isChecked && !manuallyUnexcluded.includes(uuid)) {
        setManuallyUnexcluded([...manuallyUnexcluded, uuid]);
      } else if (isChecked && manuallyUnexcluded.includes(uuid)) {
        setManuallyUnexcluded(manuallyUnexcluded.filter((id) => id !== uuid));
      }
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      onExcludedContractsChange([]);
    } else {
      const allUuids = validContracts.map((contract) => contract.contrato.uuid);
      onExcludedContractsChange(allUuids);
    }
    setSelectAll(!selectAll);
  };

  const handleClearSelection = () => {
    onExcludedContractsChange([]);
    setSelectAll(false);
  };

  const selectedCount = excludedContracts.length;
  const totalValidContracts = validContracts.length;

  if (!data) return null;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          height: '80vh',
          maxHeight: '800px',
          borderRadius: '10px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: { xs: '18px', md: '20px' },
          fontWeight: 'bold',
          textAlign: 'center',
          color: '#5e894f',
          borderBottom: '2px solid #5e894f',
          pb: 2,
          backgroundColor: '#f2f2f2',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
          <InfoIcon sx={{ color: '#5e894f' }} />
          Información de cupón generado
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '16px', md: '24px' },
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {data.confirm && (
          <Box
            sx={{
              p: 2,
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              border: '2px solid #5e894f',
              bgcolor: '#5e894f',
              mt: 2,
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                fontWeight: 'medium',
                color: 'white',
                textAlign: 'center',
              }}
            >
              {data.message}
            </Typography>
          </Box>
        )}

        {data.navigate && (
          <Box
          sx={{
            p: 2,
            backgroundColor: '#f5f5f5',
            borderRadius: '8px',
            border: '2px solid #5e894f',
            mt: 2,
          }}
          >
            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                fontWeight: 'medium',
                color: '#5e894f',
                textAlign: 'center',
              }}
            >
              {MESSAGES.NAVIGATE_PROMPT}
            </Typography>
          </Box>
        )}

        {data.contracts && data.contracts.length > 0 && (
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: 2,
                mb: 2,
                p: 2,
                backgroundColor: '#f2f2f2',
                borderRadius: '8px',
                border: '1px solid #d6d8d5',
              }}
            >
              <CustomTextField
                size="small"
                placeholder="Buscar por número de contrato..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: '#5e894f' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  flex: 1,
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: 'white',
                    '& fieldset': {
                      borderColor: '#5e894f',
                    },
                    '&:hover fieldset': {
                      borderColor: '#5e894f',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#5e894f',
                    },
                  },
                }}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <CustomButton
                  size="small"
                  variant="base"
                  sx={{ height: '42px', marginTop: '10px'}}
                  startIcon={<SelectAllIcon />}
                  onClick={handleSelectAll}
                >
                  {selectAll ? 'Deseleccionar' : 'Seleccionar'} Todo
                </CustomButton>

                <CustomButton
                  size="small"
                  variant="warning"
                  sx={{ height: '42px', marginTop: '10px'}}
                  startIcon={<ClearAllIcon />}
                  onClick={handleClearSelection}
                  disabled={selectedCount === 0}
                >
                  Limpiar
                </CustomButton>
              </Box>
            </Box>

            <Box
              sx={{
                display: 'flex',
                gap: 1,
                mb: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <StatusChip
                label={`Total: ${totalValidContracts} contratos`}
                color="info"
                variant="outlined"
                sx={{ borderColor: '#5e894f', color: '#5e894f', fontWeight: 'medium' }}
              />
              <StatusChip
                label={`Excluidos: ${selectedCount}`}
                color={selectedCount > 0 ? 'error' : 'default'}
                variant="outlined"
                sx={{ fontWeight: 'medium' }}
              />
              {searchTerm && (
                <StatusChip
                  label={`Filtrados: ${filteredContracts.length}`}
                  color="info"
                  variant="outlined"
                  sx={{ borderColor: '#d6d8d5', color: '#5e894f' }}
                />
              )}
            </Box>

            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                border: '1px solid #d6d8d5',
                borderRadius: '8px',
                backgroundColor: 'white',
                minHeight: '200px',
              }}
            >
              {filteredContracts.length === 0 ? (
                <Box
                  sx={{
                    p: 4,
                    textAlign: 'center',
                    color: '#5e894f',
                  }}
                >
                  <Typography>No se encontraron contratos</Typography>
                </Box>
              ) : (
                sortedContracts.map((contract, index) => {
                  const listLength = sortedContracts.length;
                  if (contract.contrato?.contract_number === undefined) {
                    return (
                      <Box
                        key={index}
                        sx={{
                          p: 2,
                          backgroundColor: '#f5f5f5',
                          borderBottom: '1px solid #d6d8d5',
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: { xs: '14px', md: '16px' },
                            color: '#d44242',
                            fontWeight: 'medium',
                          }}
                        >
                          {contract.message}
                        </Typography>
                      </Box>
                    );
                  }

                  const {
                    issuedRounded,
                    remainingRounded,
                    extraRounded,
                    isFinishing,
                  } = contract.meta;
                  const textColor = isFinishing ? '#d44242' : '#5e894f';

                  return (
                    <Box
                      key={index}
                      sx={{
                        borderBottom:
                          index < listLength - 1 ? '1px solid #f5f5f5' : 'none',
                        '&:hover': {
                          backgroundColor: '#f2f2f2',
                        },
                      }}
                    >
                      <FormControlLabel
                        control={
                          <CustomCheckbox
                            checked={excludedContracts.includes(contract.contrato.uuid)}
                            onChange={(e) =>
                              handleCheckboxChange(contract, e.target.checked)
                            }
                          />
                        }
                        label={
                          <Box sx={{ py: 1, width: '100%' }}>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                flexWrap: 'wrap',
                                gap: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: { xs: '14px', md: '16px' },
                                  fontWeight: 'bold',
                                  color: textColor,
                                }}
                              >
                                Contrato: {contract.contrato.contract_number}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                  <StatusChip
                                    size="small"
                                    variant="outlined"
                                    color={isFinishing ? 'error' : 'info'}
                                    label={`${issuedRounded.toFixed(2)} cuotas`}
                                    sx={{ borderColor: '#d6d8d5', color: textColor, fontSize: '12px' }}
                                  />
                                  <StatusChip
                                    size="small"
                                    variant="outlined"
                                    color={isFinishing ? 'error' : 'info'}
                                    label={`${remainingRounded.toFixed(2)} cuotas faltan`}
                                    sx={{ borderColor: '#d6d8d5', color: textColor, fontSize: '12px' }}
                                  />
                                  {extraRounded > 0 && (
                                    <StatusChip
                                      size="small"
                                      variant="outlined"
                                      color="error"
                                      label={`Se generaron ${extraRounded.toFixed(2)} cuotas de más`}
                                      sx={{ borderColor: '#d44242', color: '#d44242', fontSize: '12px' }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            </Box>
                        }
                        sx={{
                          display: 'flex',
                          width: '100%',
                          m: 0,
                          p: 2,
                          alignItems: 'flex-start',
                        }}
                      />
                    </Box>
                  );
                })
              )}
            </Box>
          </Box>
        )}

        {!data.confirm && !data.navigate && !data.contracts && (
          <Box
            sx={{
              p: 3,
              textAlign: 'center',
              backgroundColor: '#f2f2f2',
              borderRadius: '8px',
              border: '1px solid #d6d8d5',
            }}
          >
            <Typography
              sx={{
                fontSize: { xs: '14px', md: '16px' },
                color: '#5e894f',
                fontWeight: 'medium',
              }}
            >
              {data.message}
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px', md: '24px' },
          gap: 2,
          borderTop: '2px solid #5e894f',
          backgroundColor: '#f2f2f2',
          justifyContent: 'space-between',
        }}
      >
        <Box>
          <CustomButton
            onClick={onClose}
            variant="danger"
          >
            {MESSAGES.CLOSE_BUTTON}
          </CustomButton>
        </Box>

        <Box sx={{ display: 'flex', gap: 2 }}>
          {data.confirm && (
            <CustomButton
              onClick={onConfirm}
              variant="base"
            >
              {MESSAGES.CONFIRM_BUTTON}
            </CustomButton>
          )}

          {data.navigate && (
            <CustomButton
              onClick={onNavigate}
              variant="contained"
            >
              {MESSAGES.VIEW_COUPONS_BUTTON}
            </CustomButton>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
